/**
 * O-Live Music Agency - Original Design System
 * 
 * This file contains the original design values for the O-Live website.
 * These values were established during the initial design phase and should
 * be preserved as the "source of truth" for the original brand identity.
 * 
 * ⚠️ WARNING: Do not modify these values unless updating the official brand guidelines.
 * These are used as fallbacks and reset values in the CMS system.
 */

// Original brand colors established in the initial design
export const ORIGINAL_BRAND_COLORS = {
  // Primary brand color - Dark blue-green, professional and sophisticated
  primary: '#0F3C4B',
  
  // Secondary brand color - Light blue-gray, complementary and elegant  
  secondary: '#61839E',
  
  // Color descriptions for reference
  descriptions: {
    primary: 'Dark blue-green - Used for main buttons, links, and primary accents',
    secondary: 'Light blue-gray - Used for text, borders, and subtle elements'
  }
} as const

// Original typography settings
export const ORIGINAL_TYPOGRAPHY = {
  primaryFont: 'Inter',
  secondaryFont: 'Inter', 
  headingFont: 'Inter',
  fontWeights: {
    body: 400,
    headings: 400,
    buttons: 400
  }
} as const

// Original logo assets
export const ORIGINAL_LOGOS = {
  main: '/assets/LOGO.jpg',
  white: '/assets/LOGO - TRANSPARENT WHITE.png',
  black: '/assets/LOGO - BLACK ON WHITE.png'
} as const

// Complete original site settings object
export const ORIGINAL_SITE_SETTINGS = {
  fonts: ORIGINAL_TYPOGRAPHY,
  theme: {
    primaryColor: ORIGINAL_BRAND_COLORS.primary,
    secondaryColor: ORIGINAL_BRAND_COLORS.secondary
  },
  logo: ORIGINAL_LOGOS
} as const

// Color palette derived from original brand colors
export const ORIGINAL_COLOR_PALETTE = {
  // Primary variations
  'primary-50': '#f0f9ff',
  'primary-100': '#e0f2fe', 
  'primary-200': '#bae6fd',
  'primary-300': '#7dd3fc',
  'primary-400': '#38bdf8',
  'primary-500': ORIGINAL_BRAND_COLORS.primary,
  'primary-600': '#0284c7',
  'primary-700': '#0369a1',
  'primary-800': '#075985',
  'primary-900': '#0c4a6e',
  
  // Secondary variations  
  'secondary-50': '#f8fafc',
  'secondary-100': '#f1f5f9',
  'secondary-200': '#e2e8f0',
  'secondary-300': '#cbd5e1',
  'secondary-400': '#94a3b8',
  'secondary-500': ORIGINAL_BRAND_COLORS.secondary,
  'secondary-600': '#475569',
  'secondary-700': '#334155',
  'secondary-800': '#1e293b',
  'secondary-900': '#0f172a'
} as const

// Design tokens for consistent spacing, borders, etc.
export const ORIGINAL_DESIGN_TOKENS = {
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  }
} as const

/**
 * Helper function to get original color with opacity
 * @param color - 'primary' or 'secondary'
 * @param opacity - opacity value between 0 and 1
 * @returns CSS color string with opacity
 */
export function getOriginalColorWithOpacity(
  color: 'primary' | 'secondary', 
  opacity: number
): string {
  const hex = ORIGINAL_BRAND_COLORS[color]
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Helper function to validate if colors match original design
 * @param primaryColor - hex color string
 * @param secondaryColor - hex color string
 * @returns boolean indicating if colors match original
 */
export function isUsingOriginalColors(
  primaryColor: string, 
  secondaryColor: string
): boolean {
  return (
    primaryColor.toLowerCase() === ORIGINAL_BRAND_COLORS.primary.toLowerCase() &&
    secondaryColor.toLowerCase() === ORIGINAL_BRAND_COLORS.secondary.toLowerCase()
  )
}

// Export individual values for easy importing
export const ORIGINAL_PRIMARY_COLOR = ORIGINAL_BRAND_COLORS.primary
export const ORIGINAL_SECONDARY_COLOR = ORIGINAL_BRAND_COLORS.secondary
